import { getCdnUrl } from '~/utils/mediaUtils'
import { buildLambdaUrl } from '~/utils/imageUtils'

/**
 * 비디오 리사이징 산출물 URL을 deterministic하게 생성한다.
 * 산출물 경로 규칙: /file/resize-video/{jobLclCd}/{yyyyMMdd}/{quality}/{basename}.mp4
 *
 * @param {string} videoSrc - 원본 비디오 경로 (예: /file/PD/20260615/abc.mp4)
 * @param {number|string|null} quality - 화질 (480, 720). null이면 기본값 480
 * @returns {string} Lambda 리사이징 경로가 포함된 CDN URL, 또는 유효하지 않으면 빈 문자열
 */
export function buildVideoUrl (videoSrc, quality = 480) {
  if (!videoSrc || typeof videoSrc !== 'string') {
    return ''
  }

  let cleanSrc = videoSrc

  // 이미 CDN URL인 경우 경로만 추출
  if (cleanSrc.startsWith('http://') || cleanSrc.startsWith('https://')) {
    try {
      const url = new URL(cleanSrc)
      cleanSrc = url.pathname
    } catch (e) {
      console.warn('[buildVideoUrl] Invalid URL:', cleanSrc)
      return ''
    }
  }

  // 기존 dims/xcdr 경로 제거
  cleanSrc = cleanSrc.replace(/\/(dims|xcdr)\/.*$/, '')

  if (quality === null) {
    return getCdnUrl(cleanSrc)
  }

  // 이미 리사이징된 경로 정리
  cleanSrc = cleanSrc.replace(/\/file\/resize-video\//, '/file/')

  // /file/로 시작하지 않으면 리사이징 불가 → 원본 CDN URL 반환
  if (!cleanSrc.startsWith('/file/')) {
    return getCdnUrl(cleanSrc)
  }

  // /file/{jobLclCd}/{yyyyMMdd}/{filename} 구조 파싱
  const pathParts = cleanSrc.replace(/^\/file\//, '').split('/')

  if (pathParts.length < 3) {
    console.warn('[buildVideoUrl] Invalid path structure:', cleanSrc)
    return getCdnUrl(cleanSrc)
  }

  const [jobLclCd, date, ...filenameParts] = pathParts
  const filename = filenameParts.join('/')

  // 확장자를 .mp4로 강제 (MediaConvert 산출물은 항상 mp4)
  const basename = filename.replace(/\.[^/.]+$/, '') + '.mp4'

  const effectiveQuality = quality ? `${quality}p` : '480p'
  const resizedUrl = `/file/resize-video/${jobLclCd}/${date}/${effectiveQuality}/${basename}`

  return getCdnUrl(resizedUrl)
}

/**
 * 비디오 리사이징 fallback 소스 배열을 생성한다.
 * fallback 순서: 지정 화질 → 반대 리사이징 화질 → 원본
 *
 * @param {string} videoSrc - 원본 비디오 경로
 * @param {number|string|null} quality - 요청 화질 (480, 720). null이면 480 기본
 * @returns {Array<{src: string, type: string}>} video source 배열 (fallback 순서)
 */
export function buildVideoSources (videoSrc, quality = 480) {
  if (!videoSrc || typeof videoSrc !== 'string') {
    return []
  }

  if (quality === null) {
    return [{ src: getCdnUrl(videoSrc), type: 'video/mp4' }]
  }

  const effectiveQuality = Number(quality) || 480
  const sources = []
  const addSource = (src) => {
    if (src && !sources.some(source => source.src === src)) {
      sources.push({ src, type: 'video/mp4' })
    }
  }

  // 1. 지정 화질
  addSource(buildVideoUrl(videoSrc, effectiveQuality))

  // 2. 480p 요청은 720p, 720p 요청은 480p를 대체 산출물로 시도한다.
  addSource(buildVideoUrl(videoSrc, effectiveQuality === 720 ? 480 : 720))

  // 3. 원본 fallback
  addSource(buildVideoUrl(videoSrc, null))

  return sources
}

/**
 * 비디오 캡처 이미지(포스터/썸네일) URL을 생성한다.
 * 이미지 Lambda가 비디오에서 캡처를 떠서 이미지로 응답한다.
 *
 * - 컴포넌트에서 사용: <daiso-image :src="videoSrc" detail="capture" size="300" />
 * - JS에서 URL 문자열이 필요할 때: getCapturedUrl(videoSrc)
 *
 * @param {string} videoSrc - 비디오 원본 경로 (예: /file/PD/20260615/abc.mp4)
 * @param {string} size - 캡처 이미지 크기 (기본 '300')
 * @returns {string} CDN이 포함된 캡처 이미지 URL
 */
export function getCapturedUrl (videoSrc, size = '300') {
  try {
    const numSize = parseInt(size, 10)
    const validSize = (numSize === 300 || numSize === 850) ? numSize : (numSize < 300 ? 300 : 850)
    return buildLambdaUrl(videoSrc, 'capture', validSize)
  } catch (e) {
    return buildLambdaUrl(videoSrc, 'capture', 850)
  }
}
